// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import { isAfter, isBefore } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import {
  type ClockOutErrorType,
  type ClockOutGeofenceValidationType,
  validateClockOutTimestamp,
} from "../../MissedPunches/utils/validateClockOutTimestamp";
import { isBoundaryError } from "../analytics/isBoundaryError";
import { TimekeepingAdjustmentBottomSheet } from "../TimekeepingAdjustmentBottomSheet";
import { getBottomSheetIntent } from "./utils/getBottomSheetIntent";
import { getDayIndicator } from "./utils/getDayIndicator";
import { inferDateFromTime } from "./utils/inferDateFromTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditClockOutTimeFormValues {
  clockOutTime?: Date;
}

interface EditClockOutTimeBottomSheetProps {
  modalState: UseModalState;
  clockOutTime?: Date;
  workplaceTimezone: string;
  clockOutButtonTappedAt?: Date;
  breakEndTime?: Date;
  clockInTime?: Date;
  shiftStartAt: Date;
  shiftEndAt: Date;
  geofenceDepartureTime?: Date;
  latestPlausibleDepartureTime?: Date;
  workerClockOut?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
  isDirectIntegrationFacility?: boolean;
  isMissedPunchRequestEnabled: boolean;
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockOut?: Date;
  shiftId?: string;
  workerId?: string;
  workplaceId?: string;
  timesheetFlow?: string;
  existingReason?: string;
  onConfirm: (clockOutTime: Date, reason: string) => void;
  onCancel?: () => void;
}

export function EditClockOutTimeBottomSheet(props: EditClockOutTimeBottomSheetProps) {
  const {
    modalState,
    clockOutTime,
    workplaceTimezone,
    clockOutButtonTappedAt,
    breakEndTime,
    clockInTime,
    shiftStartAt,
    shiftEndAt,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    workerClockOut,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
    isDirectIntegrationFacility = false,
    isMissedPunchRequestEnabled,
    isEnforceGeofenceTimestampsEnabled = false,
    verifiedClockOut,
    shiftId,
    workerId,
    workplaceId,
    timesheetFlow,
    existingReason,
    onConfirm,
    onCancel,
  } = props;

  const [saveAttempted, setSaveAttempted] = useState(false);
  const [isPickerScrolling, setIsPickerScrolling] = useState(false);

  const { setValue, watch, reset } = useForm<EditClockOutTimeFormValues>({
    defaultValues: {
      clockOutTime: isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined,
    },
  });

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      const resetValue = isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined;
      reset({ clockOutTime: resetValue });
      setSaveAttempted(false);
    }
  }, [modalState.modalIsOpen, clockOutTime, reset, workplaceTimezone]);

  const currentLocalClockOutTime = watch("clockOutTime");

  const currentClockOutTime = isDefined(currentLocalClockOutTime)
    ? getUtcTimeFromPicker(currentLocalClockOutTime, workplaceTimezone)
    : undefined;

  const getValidationMessages = (
    selectedTime?: Date
  ): {
    errorMessage?: string;
    warningMessage?: string;
    infoMessage?: string;
    errorType?: ClockOutErrorType;
    geofenceValidationType?: ClockOutGeofenceValidationType;
  } => {
    if (!isDefined(selectedTime)) {
      return {};
    }

    if (isMissedPunchRequestEnabled && isDefined(clockInTime) && isDefined(shiftEndAt)) {
      const validationResult = validateClockOutTimestamp({
        selectedTime,
        clockInTime,
        geofenceDepartureTime,
        latestPlausibleDepartureTime,
        shiftEndAt,
        workerClockOut,
        isGeofenceFacility,
        isNfcOrMeshFacility,
        isDirectIntegrationFacility,
        isEnforceGeofenceTimestampsEnabled,
        verifiedClockOut,
        workplaceTimezone,
      });

      if (!validationResult.isValid) {
        return {
          errorMessage: validationResult.message,
          errorType: validationResult.errorType,
          geofenceValidationType: validationResult.geofenceValidationType,
        };
      }

      return {};
    }

    // Legacy validation (when feature flag is off)
    if (isDefined(clockOutButtonTappedAt) && isAfter(selectedTime, clockOutButtonTappedAt)) {
      return {
        errorMessage: `Please select a clock out time at or before ${formatTimeWithTimezone(
          clockOutButtonTappedAt,
          workplaceTimezone,
          false
        )} — the time you tapped 'Clock Out'`,
      };
    }

    if (isDefined(breakEndTime) && isBefore(selectedTime, breakEndTime)) {
      return { errorMessage: "You cannot clock out before your break ends." };
    }

    if (isDefined(clockInTime) && !isAfter(selectedTime, clockInTime)) {
      return { errorMessage: "You cannot clock out before or at your clock in time." };
    }

    return {};
  };

  const { errorMessage, warningMessage, infoMessage, errorType, geofenceValidationType } =
    getValidationMessages(currentClockOutTime);

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
      });

      const timeToSet = inferResult.success ? inferResult.date : newTime;
      setValue("clockOutTime", timeToSet, { shouldDirty: true });
      setSaveAttempted(false);
    }
  };

  // Called by the shell when the user taps Next/Save on the picker step.
  // Returns false to block step advancement when there is a validation error.
  const handlePickerConfirm = (): boolean => {
    setSaveAttempted(true);

    if (isBoundaryError(errorType)) {
      logEvent(APP_V2_USER_EVENTS.MISSED_PUNCH_REQUEST_TIMESHEET_BOUNDARY_VIOLATION_ATTEMPTED, {
        actor: "worker",
        clockAction: "clock-out",
        errorType,
        shiftId,
        workplaceId,
        selectedTime: currentClockOutTime?.toISOString(),
        geofenceDepartureTime: geofenceDepartureTime?.toISOString(),
        shiftEndAt: shiftEndAt?.toISOString(),
        verifiedClockOut: verifiedClockOut?.toISOString(),
        latestPlausibleDepartureTime: latestPlausibleDepartureTime?.toISOString(),
        geofenceValidationType,
      });
    }

    if (errorMessage) {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_FORM_VALIDATION_ERROR, {
        shiftId,
        workerId,
        workplaceId,
        error: errorMessage,
        errorType,
        timekeepingAction: "clock-out",
        timesheetFlow,
      });
    }

    return !errorMessage;
  };

  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentClockOutTime,
    referenceTimestamp: shiftEndAt,
    workplaceTimezone,
  });

  const {
    intent: validationIntent,
    intentContent: validationIntentContent,
    intentIcon: validationIntentIcon,
  } = getBottomSheetIntent({ saveAttempted, errorMessage, warningMessage, infoMessage });

  const requiresApproval =
    isDefined(currentClockOutTime) && isDefined(clockOutTime) && currentClockOutTime > clockOutTime;
  const showApprovalIntent = requiresApproval && !isPickerScrolling && !validationIntent;

  return (
    <TimekeepingAdjustmentBottomSheet
      modalState={modalState}
      intent={validationIntent ?? (showApprovalIntent ? "neutral" : undefined)}
      intentContent={
        validationIntentContent ??
        (showApprovalIntent ? "You are requesting a later clock out time." : undefined)
      }
      intentIcon={validationIntentIcon ?? (showApprovalIntent ? "info" : undefined)}
      pickerTitle="Adjust clock out"
      hasChanges={isDefined(currentClockOutTime)}
      isPickerScrolling={isPickerScrolling}
      isPickerValid={isDefined(currentClockOutTime)}
      requiresApproval={requiresApproval}
      reasonTitle="Why did you adjust your clock out time?"
      existingReason={existingReason}
      reasonHelperText="Edit your message to the workplace to explain why you adjusted your clock out time."
      onPickerConfirm={handlePickerConfirm}
      onConfirm={(reason) => {
        if (isDefined(currentClockOutTime)) {
          onConfirm(currentClockOutTime, reason);
        }
      }}
      onCancel={onCancel}
    >
      <WheelTimePicker
        preventParentTouchStart
        value={currentLocalClockOutTime}
        dayIndicator={dayIndicator}
        containerSx={{
          alignSelf: "center",
        }}
        onChange={handleTimeChange}
        onScrollingChange={setIsPickerScrolling}
      />
    </TimekeepingAdjustmentBottomSheet>
  );
}
